import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { menuAPI, settingsAPI, orderAPI } from '../utils/api';
import { generateReceiptHTML, generateKitchenTicketHTML, generateBeverageTicketHTML, printWithDialog } from '../utils/webPrint';
import axios from 'axios';

import {
  POSSidebar, POSHeader, DashboardView, MenuView,
  OrderDetailView, OrderCompleteModal, SplitBillModal,
  translations, BEVERAGE_CATEGORIES
} from '../components/pos';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ManualBillPage = () => {
  const { user } = useUser();

  // Language
  const [lang, setLang] = useState('en');
  const t = translations[lang];

  // Data
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Orders & Tables
  const [pendingOrders, setPendingOrders] = useState([]);
  const [tableSessions, setTableSessions] = useState([]);

  // View state
  const [viewMode, setViewMode] = useState('dashboard');
  const [selectedTable, setSelectedTable] = useState(null);

  // Menu/Cart state
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [tableInput, setTableInput] = useState('');

  // Checkout
  const [discountPercent, setDiscountPercent] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);

  // Bill options for closing table
  const [billDiscountPercent, setBillDiscountPercent] = useState(0);
  const [billPaymentMethod, setBillPaymentMethod] = useState('cash');

  // Sound & Notifications
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [notifiedOrders, setNotifiedOrders] = useState(new Set());

  // Split bill state
  const [showSplitBill, setShowSplitBill] = useState(false);
  const [splitPeople, setSplitPeople] = useState([
    { label: 'Person 1', itemIndices: [], paymentMethod: 'cash' },
    { label: 'Person 2', itemIndices: [], paymentMethod: 'cash' },
  ]);
  const [activeSplitPerson, setActiveSplitPerson] = useState(0);
  const [splitResult, setSplitResult] = useState(null);
  const audioRef = useRef(null);

  // Time
  const [currentTime, setCurrentTime] = useState(new Date());

  // Load data
  useEffect(() => {
    loadAllData();
    const clockInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    const refreshInterval = setInterval(loadAllData, 10000);
    return () => {
      clearInterval(clockInterval);
      clearInterval(refreshInterval);
    };
  }, []);

  const loadAllData = async () => {
    try {
      const [menuRes, settingsRes, ordersRes, sessionsRes] = await Promise.all([
        menuAPI.getAll(),
        settingsAPI.get(),
        orderAPI.getAll(),
        axios.get(`${API_URL}/api/table-sessions`)
      ]);

      setMenuItems(menuRes.data || []);
      setSettings(settingsRes.data);
      setTableSessions(sessionsRes.data || []);

      const incoming = (ordersRes.data || []).filter(o => o.status === 'pending_approval');
      incoming.forEach(order => {
        if (!notifiedOrders.has(order.id)) {
          playNotificationSound();
          setNotifiedOrders(prev => new Set([...prev, order.id]));
        }
      });
      setPendingOrders(incoming);

      const cats = [...new Set((menuRes.data || []).map(item => item.category))].filter(Boolean);
      setCategories(cats);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const playNotificationSound = () => {
    if (!soundEnabled) return;
    try {
      if (settings?.notification_sound_url) {
        const audio = new Audio(settings.notification_sound_url);
        audio.play().catch(() => {});
      } else {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 800;
        gain.gain.value = 0.3;
        osc.start();
        osc.stop(ctx.currentTime + 0.2);
      }
    } catch (e) {}
  };

  // Filter menu items
  const filteredItems = menuItems.filter(item => {
    const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
    const itemName = item.name?.en || item.name || '';
    const matchesSearch = itemName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Cart functions
  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(c => c.id === item.id);
      if (existing) {
        return prev.map(c => c.id === item.id ? {...c, quantity: c.quantity + 1} : c);
      }
      return [...prev, {...item, quantity: 1}];
    });
  };

  const updateCartQty = (id, delta) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = Math.max(0, item.quantity + delta);
        return newQty === 0 ? null : {...item, quantity: newQty};
      }
      return item;
    }).filter(Boolean));
  };

  // Calculate totals
  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const discount = subtotal * (discountPercent / 100);
  const cardFee = paymentMethod === 'card' && settings?.enable_credit_card_fee
    ? (subtotal - discount) * (settings?.credit_card_fee_percent || 3) / 100
    : 0;
  const total = subtotal - discount + cardFee;

  // Transfer order to Kitchen/Bar
  const transferOrder = async (order) => {
    try {
      await orderAPI.transfer(order.id);
      const foodItems = order.items.filter(item => !BEVERAGE_CATEGORIES.includes(item.category));
      const drinkItems = order.items.filter(item => BEVERAGE_CATEGORIES.includes(item.category));
      if (foodItems.length > 0) {
        const kitchenHTML = generateKitchenTicketHTML({...order, items: foodItems}, settings);
        printWithDialog(kitchenHTML);
      }
      if (drinkItems.length > 0) {
        const barHTML = generateBeverageTicketHTML({...order, items: drinkItems}, settings);
        printWithDialog(barHTML);
      }
      await loadAllData();
    } catch (error) {
      console.error('Error transferring order:', error);
      alert('Failed to transfer order');
    }
  };

  // Print bill for table
  const printTableBill = (session) => {
    const orderData = {
      order_number: session.table,
      table: session.table,
      items: (session.orders || []).flatMap(o => o.items || []),
      created_at: session.created_at
    };
    const html = generateReceiptHTML(orderData, settings);
    printWithDialog(html);
  };

  // Create new order
  const createOrder = async () => {
    if (cart.length === 0 || !tableInput) return;
    setIsProcessing(true);
    try {
      const orderData = {
        table: tableInput,
        total: total,
        items: cart.map(item => ({
          menu_item_id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          category: item.category,
          special_note: ''
        })),
        source: 'manual_pos'
      };
      await axios.post(`${API_URL}/api/orders`, orderData);
      setOrderComplete(true);
      await loadAllData();
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Failed to create order');
    }
    setIsProcessing(false);
  };

  // Close table and print final bill
  const closeTable = async (session) => {
    try {
      const sessionSubtotal = (session.orders || []).reduce((sum, o) =>
        sum + (o.items || []).reduce((s, i) => s + (i.price * i.quantity), 0), 0
      );
      await axios.post(`${API_URL}/api/bill/table/finalize`, {
        session_id: session.id,
        discount_percent: billDiscountPercent,
        payment_method: billPaymentMethod
      });
      const discountAmt = sessionSubtotal * (billDiscountPercent / 100);
      const afterDiscount = sessionSubtotal - discountAmt;
      const cardFeePercent = settings?.credit_card_fee_percent || 5;
      const feeAmt = billPaymentMethod === 'card' && settings?.enable_credit_card_fee
        ? afterDiscount * cardFeePercent / 100
        : 0;
      const finalTotal = afterDiscount + feeAmt;
      const billData = {
        order_number: session.table,
        table: session.table,
        items: (session.orders || []).flatMap(o => o.items || []),
        subtotal: sessionSubtotal,
        discount_percent: billDiscountPercent,
        discount: discountAmt,
        card_fee_percent: billPaymentMethod === 'card' ? cardFeePercent : 0,
        card_fee: feeAmt,
        total: finalTotal,
        payment_method: billPaymentMethod,
        created_at: session.created_at
      };
      const html = generateReceiptHTML(billData, settings);
      printWithDialog(html);
      await loadAllData();
      setSelectedTable(null);
      setViewMode('dashboard');
      setBillDiscountPercent(0);
      setBillPaymentMethod('cash');
    } catch (error) {
      console.error('Error closing table:', error);
      alert('Failed to close table');
    }
  };

  // Split bill functions
  const openSplitBill = () => {
    setSplitPeople([
      { label: `${t.person} 1`, itemIndices: [], paymentMethod: 'cash' },
      { label: `${t.person} 2`, itemIndices: [], paymentMethod: 'cash' },
    ]);
    setActiveSplitPerson(0);
    setSplitResult(null);
    setShowSplitBill(true);
  };

  const getSplitAllItems = () => {
    if (!selectedTable) return [];
    return (selectedTable.orders || []).flatMap(o => o.items || []);
  };

  const toggleItemForPerson = (itemIdx) => {
    setSplitPeople(prev => {
      return prev.map((p, i) => {
        if (i === activeSplitPerson) {
          const has = p.itemIndices.includes(itemIdx);
          return { ...p, itemIndices: has ? p.itemIndices.filter(x => x !== itemIdx) : [...p.itemIndices, itemIdx] };
        }
        return { ...p, itemIndices: p.itemIndices.filter(x => x !== itemIdx) };
      });
    });
  };

  const addSplitPerson = () => {
    setSplitPeople(prev => [
      ...prev,
      { label: `${t.person} ${prev.length + 1}`, itemIndices: [], paymentMethod: 'cash' }
    ]);
  };

  const removeSplitPerson = (idx) => {
    if (splitPeople.length <= 2) return;
    setSplitPeople(prev => prev.filter((_, i) => i !== idx));
    if (activeSplitPerson >= splitPeople.length - 1) setActiveSplitPerson(0);
  };

  const splitEvenly = () => {
    const numStr = prompt(t.howManyPeople || 'How many people?', '2');
    if (!numStr) return;
    const num = parseInt(numStr, 10);
    if (isNaN(num) || num < 2 || num > 20) {
      alert('Please enter a number between 2 and 20');
      return;
    }
    const allItems = getSplitAllItems();
    const people = Array.from({ length: num }, (_, i) => ({
      label: `${t.person} ${i + 1}`,
      itemIndices: [],
      paymentMethod: 'cash',
    }));
    allItems.forEach((_, idx) => {
      people[idx % num].itemIndices.push(idx);
    });
    setSplitPeople(people);
    setActiveSplitPerson(0);
    setSplitResult(null);
  };

  const confirmSplitBill = async () => {
    const allItems = getSplitAllItems();
    const allAssigned = splitPeople.every(p => p.itemIndices.length > 0);
    const totalAssigned = splitPeople.reduce((s, p) => s + p.itemIndices.length, 0);
    if (!allAssigned) {
      alert('Each person must have at least one item');
      return;
    }
    if (totalAssigned < allItems.length) {
      if (!window.confirm(`${allItems.length - totalAssigned} items are unassigned. They will not be billed. Continue?`)) return;
    }
    try {
      const resp = await axios.post(`${API_URL}/api/bill/table/split`, {
        session_id: selectedTable.id,
        splits: splitPeople.map(p => ({
          label: p.label,
          item_indices: p.itemIndices,
          payment_method: p.paymentMethod,
        })),
        discount_percent: billDiscountPercent,
      });
      setSplitResult(resp.data);
    } catch (error) {
      console.error('Error splitting bill:', error);
      alert('Failed to split bill');
    }
  };

  const printSplitBills = () => {
    if (!splitResult) return;
    splitResult.splits.forEach((split, i) => {
      const billData = {
        order_number: `${selectedTable.table}-${split.label}`,
        table: selectedTable.table,
        items: split.items,
        subtotal: split.subtotal,
        discount_percent: split.discount_percent,
        discount: split.discount_amount,
        card_fee_percent: split.card_fee_percent,
        card_fee: split.card_fee,
        total: split.total,
        payment_method: split.payment_method,
        created_at: selectedTable.created_at,
      };
      setTimeout(() => {
        const html = generateReceiptHTML(billData, settings);
        printWithDialog(html);
      }, i * 500);
    });
  };

  const closeSplitBill = async () => {
    printSplitBills();
    setShowSplitBill(false);
    setSplitResult(null);
    await loadAllData();
    setSelectedTable(null);
    setViewMode('dashboard');
    setBillDiscountPercent(0);
  };

  // Update individual order status
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await orderAPI.updateStatus(orderId, newStatus);
      await loadAllData();
      if (selectedTable) {
        const updatedSessions = await axios.get(`${API_URL}/api/table-sessions`);
        const updatedTable = updatedSessions.data.find(s => s.id === selectedTable.id);
        if (updatedTable) setSelectedTable(updatedTable);
      }
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const updateKitchenStatus = async (orderId, status) => {
    try {
      await orderAPI.updateKitchenStatus(orderId, status);
      await loadAllData();
      if (selectedTable) {
        const updatedSessions = await axios.get(`${API_URL}/api/table-sessions`);
        const updatedTable = updatedSessions.data.find(s => s.id === selectedTable.id);
        if (updatedTable) setSelectedTable(updatedTable);
      }
    } catch (error) {
      console.error('Error updating kitchen status:', error);
    }
  };

  const updateBeverageStatus = async (orderId, status) => {
    try {
      await orderAPI.updateBeverageStatus(orderId, status);
      await loadAllData();
      if (selectedTable) {
        const updatedSessions = await axios.get(`${API_URL}/api/table-sessions`);
        const updatedTable = updatedSessions.data.find(s => s.id === selectedTable.id);
        if (updatedTable) setSelectedTable(updatedTable);
      }
    } catch (error) {
      console.error('Error updating beverage status:', error);
    }
  };

  const cancelOrder = async (orderId) => {
    if (!window.confirm(lang === 'en' ? 'Cancel this order?' : 'Huy don nay?')) return;
    try {
      await orderAPI.updateStatus(orderId, 'cancelled');
      await loadAllData();
      if (selectedTable) {
        const updatedSessions = await axios.get(`${API_URL}/api/table-sessions`);
        const updatedTable = updatedSessions.data.find(s => s.id === selectedTable.id);
        if (updatedTable) setSelectedTable(updatedTable);
      }
    } catch (error) {
      console.error('Error cancelling order:', error);
    }
  };

  const resetOrder = () => {
    setCart([]);
    setTableInput('');
    setDiscountPercent(0);
    setPaymentMethod('cash');
    setOrderComplete(false);
  };

  const startNewOrder = (target) => {
    if (target === 'dashboard') {
      setViewMode('dashboard');
      resetOrder();
    } else if (target === 'menu') {
      setTableInput('');
      setCart([]);
      setViewMode('menu');
    } else {
      // table number passed
      setTableInput(target || '');
      setCart([]);
      setViewMode('menu');
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400 font-bold">Loading POS...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-900 text-white overflow-hidden font-sans">
      <POSSidebar
        viewMode={viewMode}
        lang={lang}
        setLang={setLang}
        startNewOrder={startNewOrder}
        t={t}
      />

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <POSHeader
          viewMode={viewMode}
          tableInput={tableInput}
          settings={settings}
          pendingOrders={pendingOrders}
          soundEnabled={soundEnabled}
          setSoundEnabled={setSoundEnabled}
          loadAllData={loadAllData}
          currentTime={currentTime}
          resetOrder={resetOrder}
          setViewMode={setViewMode}
          t={t}
        />

        {viewMode === 'dashboard' && (
          <DashboardView
            pendingOrders={pendingOrders}
            tableSessions={tableSessions}
            transferOrder={transferOrder}
            startNewOrder={startNewOrder}
            setSelectedTable={setSelectedTable}
            setViewMode={setViewMode}
            t={t}
          />
        )}

        {viewMode === 'menu' && (
          <MenuView
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            activeCategory={activeCategory}
            setActiveCategory={setActiveCategory}
            categories={categories}
            filteredItems={filteredItems}
            cart={cart}
            addToCart={addToCart}
            updateCartQty={updateCartQty}
            tableInput={tableInput}
            setTableInput={setTableInput}
            discountPercent={discountPercent}
            setDiscountPercent={setDiscountPercent}
            subtotal={subtotal}
            discount={discount}
            total={total}
            createOrder={createOrder}
            isProcessing={isProcessing}
            t={t}
          />
        )}

        {viewMode === 'orderDetail' && selectedTable && (
          <OrderDetailView
            selectedTable={selectedTable}
            setSelectedTable={setSelectedTable}
            setViewMode={setViewMode}
            setTableInput={setTableInput}
            billDiscountPercent={billDiscountPercent}
            setBillDiscountPercent={setBillDiscountPercent}
            billPaymentMethod={billPaymentMethod}
            setBillPaymentMethod={setBillPaymentMethod}
            settings={settings}
            printTableBill={printTableBill}
            openSplitBill={openSplitBill}
            closeTable={closeTable}
            transferOrder={transferOrder}
            updateOrderStatus={updateOrderStatus}
            updateKitchenStatus={updateKitchenStatus}
            updateBeverageStatus={updateBeverageStatus}
            cancelOrder={cancelOrder}
            t={t}
          />
        )}
      </main>

      {orderComplete && (
        <OrderCompleteModal
          tableInput={tableInput}
          resetOrder={resetOrder}
          setViewMode={setViewMode}
          t={t}
        />
      )}

      <SplitBillModal
        selectedTable={selectedTable}
        showSplitBill={showSplitBill}
        setShowSplitBill={setShowSplitBill}
        splitPeople={splitPeople}
        setSplitPeople={setSplitPeople}
        activeSplitPerson={activeSplitPerson}
        setActiveSplitPerson={setActiveSplitPerson}
        splitResult={splitResult}
        setSplitResult={setSplitResult}
        billDiscountPercent={billDiscountPercent}
        setBillDiscountPercent={setBillDiscountPercent}
        settings={settings}
        getSplitAllItems={getSplitAllItems}
        toggleItemForPerson={toggleItemForPerson}
        addSplitPerson={addSplitPerson}
        removeSplitPerson={removeSplitPerson}
        splitEvenly={splitEvenly}
        confirmSplitBill={confirmSplitBill}
        closeSplitBill={closeSplitBill}
        t={t}
      />

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default ManualBillPage;
